import React, { Suspense, useRef, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { number, object, string } from 'yup'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { Button, Color, FormField, Radius, Spacing } from 'zens-storybook'
import { Draft, UserInviteData, useStore, useUserMutation } from 'zens-store'
import { useRouter, useNumberParams, Link } from 'zens-router'
import { UserEmail, UserLanguage, UserRoleSelect, NavigationConfirm, LayoutHeader } from '@dms/component'
import { ValidationError } from '@dms/helper'
import { useBreadcrumb } from '@dms/hook'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
  block: {
    marginBottom: Spacing.L,
    borderBottomWidth: 1,
    borderBottomColor: Color.calmGrey,
  },
  bottom: {
    marginTop: 80,
    flexDirection: 'row',
    marginHorizontal: -Spacing.XS,
  },
  button: {
    marginHorizontal: Spacing.XS,
  },
  // location: {
  //   zIndex: 100,
  // },
  language: {
    minHeight: 110,
  },
})

const UserInviteScreen = () => {
  const router = useRouter()
  const store = useStore()
  const { message } = store
  const [{ companyId }] = useNumberParams()
  const draft = useRef(new Draft<UserInviteData>({ id: 0 })).current
  const [errors, setErrors] = useState<Record<string, string>>({})
  const { t } = useTranslation()
  const mutation = useUserMutation({ companyId })

  const breadcrumb = useBreadcrumb([
    {
      title: t('Users'),
      href: `/companies/${companyId}/users`,
    },
    {
      title: t('Invite new user'),
    },
  ])
  const update = (key: string) => (value: unknown) => {
    return draft.update(key, value)
  }

  const save = async () => {
    try {
      const schema = object({
        email: string().required(t('Email address is required')).email(t('Invalid email address')),
        role_id: number().required(t('Role is required')),
      })
      schema.validateSync(draft.data, { abortEarly: false })
    } catch (error) {
      const { inner } = error as ValidationError
      setErrors(
        inner.reduce((acc, validationError) => {
          const { path, message } = validationError
          return { ...acc, [path!]: message }
        }, {}),
      )
      return
    }
    setErrors({})
    try {
      await mutation.invite(draft)
      message.add({
        message: t('Invitation was sent successfully'),
        status: 'success',
      })
      router.push(`/companies/${companyId}/users`)
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`USER: Invitation failed: ${errorMessage}`)
      message.add({
        message: `${t('Invitation could not be sent')}. ${errorMessage}`,
        status: 'error',
      })
    }
  }

  return (
    <View style={styles.container}>
      <LayoutHeader breadcrumb={breadcrumb} title={t('Invite new user')} />
      <View style={styles.content}>
        <View style={styles.block}>
          <UserEmail
            value={draft.data.email || ''}
            onChangeText={update('email')}
            error={errors.email}
            testID="UserInvite.Email"
          />
          <FormField
            label={t('Language')}
            style={styles.language}
            error={errors.language}
            help={t('The language of the email that will be sent to this user')}
          >
            <UserLanguage
              value={draft.data.language}
              onSelect={update('language')}
              error={errors.language}
              testID="UserInvite.Language"
            />
          </FormField>
        </View>
        {/* <View style={[styles.block, styles.location]}>
          <UserLocation value={draft.data.location_id} onSelect={update('location_id')} error={errors.location} />
        </View> */}
        <Suspense fallback={<UserRoleSelect.Skeleton />}>
          <UserRoleSelect
            companyId={companyId}
            value={draft.data.role_id}
            onSelect={update('role_id')}
            error={errors.role}
            testID="UserInvite.Role"
          />
        </Suspense>
        <View style={styles.bottom}>
          <Button title={t('Send invitation')} size="L" style={styles.button} onPress={save} testID="UserInvite.Save" />
          <Link href={`/companies/${companyId}/users`} asChild>
            <Button title={t('Cancel')} light size="L" style={styles.button} testID="UserInvite.Cancel" />
          </Link>
        </View>
      </View>
      <NavigationConfirm draft={draft} />
    </View>
  )
}

export default observer(UserInviteScreen)
